import axios from 'axios';

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN || 'http://localhost:3001';
const DISCOVERY_BASE = `${API_DOMAIN}/ai-agent/discovery`;
const AI_AGENT_EVIDENCES_BASE = `${API_DOMAIN}/ai-agent/evidences`;

export const DISCOVERY_SESSION_USER_MESSAGE_LIMIT_MESSAGE =
  "You've reached the limit for this session. To continue, please start a new session.";

// ----- Types (frontend-facing; ids as string) -----

export interface Artifact {
  /** Present once artifacts have been persisted to DISCOVERY_SESSION_ARTIFACTS. */
  _id?: string;
  alias: string;
  title: string;
  description: string;
  type?: string;
  evidence_ids: string[];
  /** Ordered idea ids for `ideas_list` artifacts. */
  idea_ids?: string[];
  count: number;
  total: number;
  proportion: number;
  proportionCiLower?: number;
  proportionCiUpper?: number;
}

export interface DiscoveryArtifactDeepLinkResponse {
  artifact: Artifact;
  /**
   * `id` is the Mongo `_id` as string (preferred URL value, matches what
   * the session list passes to `setSessionId`). `sessionId` is the stable
   * UUID-like string. `getDiscoverySessionByIdOrSessionId` accepts either.
   */
  session: { id?: string; sessionId: string; sessionName?: string };
  messageId: string;
  message?: string;
}

/** Same structure as app ActiveFilter[]. Compatible with FiltersManager / idea query pipeline. */
export interface SessionFilter {
  parent: string;
  type: string;
  selected?: unknown[];
  selectedLabels?: string[];
  selectedFilterMap?: Record<string, string>;
  name?: string;
  operator?: string;
  query?: string;
  filterObject?: 'evidence' | 'idea';
}

export interface DiscoverySession {
  id?: string;
  accountId: string;
  userId: string;
  sessionId: string;
  sessionName?: string;
  messageCount: number;
  createdAt: string;
  lastUsedAt: string;
  isActive: boolean;
  isShared?: boolean;
  chatStatus?: 'idle' | 'processing' | 'error';
  chatStatusUpdatedAt?: string;
  chatStatusError?: string;
  sessionFilters?: SessionFilter[];
  userMessageCount?: number;
  userMessageLimit?: number;
  isUserMessageLimitReached?: boolean;
  parentSessionId?: string;
}

export interface DiscoverySessionMessage {
  /** Mongo `_id` serialized as string — preferred id field on responses. */
  _id?: string;
  /** Legacy alias some callers read via `msg.id`. Kept for backward compat. */
  id?: string;
  sessionId: string;
  userId: string;
  messageType: 'user' | 'agent' | 'idea_created';
  content: string;
  timestamp: string;
  metadata?: {
    fullResponse?: Record<string, unknown>;
    artifacts?: Artifact[];
    /** Ordered real ObjectIds for an ideas-list span (post alias mapping). */
    ideasListIds?: string[];
    /** Short alias → ObjectId map for ideas-list spans (I0, I1, …). */
    ideaIdsMap?: Record<string, string>;
  };
  feedback?: {
    rating: 'helpful' | 'not_helpful';
    updatedAt: string;
  };
  /**
   * When true the message is hidden in the chat thread (e.g. a CREATE_IDEA
   * background prompt).  When explicitly false it should always be visible
   * even if its content would normally match the hidden-message filter.
   */
  hidden?: boolean;
}

export interface ContextSample {
  id: string;
  type: 'evidence' | 'idea';
  title: string;
  summary?: string;
}

export interface DiscoveryContextState {
  activeFilters: unknown[];
  evidenceIds: string[];
  ideaIds: string[];
  samples: ContextSample[];
  embeddedSubsetId?: string;
  summary?: string;
}

export interface InsightItem {
  title: string;
  body: string;
  type: 'trend' | 'pattern' | 'anomaly' | 'summary' | 'aggregation';
  relatedEvidenceIds?: string[];
  relatedIdeaIds?: string[];
  aggregationData?: Record<string, unknown>;
}

export interface DiscoverySessionInsight {
  id?: string;
  sessionId: string;
  messageId: string;
  timestamp: string;
  contextVersion: number;
  query: string;
  insights: InsightItem[];
}

// ----- Request / Response -----

export interface SendDiscoveryMessageRequest {
  message: string;
  sessionId: string;
  uiFilters?: SessionFilter[];
  /** When true the backend should store the message as hidden. */
  hidden?: boolean;
  /**
   * Opts this send into the backend's idempotent chat-request row. Sending it
   * lets a dropped browser request be recovered verbatim from
   * `GET /chat/requests/:clientRequestId` instead of being lost, and stops a
   * retry from starting a second agent run.
   *
   * Must be fresh per user send — it is the dedupe key, so reusing one would
   * replay the earlier answer instead of running the new question.
   */
  clientRequestId?: string;
}

export interface SendDiscoveryMessageResponse {
  success: boolean;
  sessionId: string;
  response?: string;
  isNewSession?: boolean;
  message?: string;
  artifacts?: Artifact[];
  isUserMessageLimitReached?: boolean;
  userMessageCount?: number;
  userMessageLimit?: number;
  /**
   * `'pending'` means the agent is still running server-side and no answer is
   * available yet — distinct from an outright failure, and the caller should
   * leave the session's own polling to surface the result.
   */
  status?: 'pending';
  /**
   * The caller's `signal` fired, so we stopped waiting for the result. The
   * send itself is unaffected and still running server-side; this says the
   * caller walked away, not that anything went wrong. A UI that was awaiting
   * the answer must not report it as a failure — by the time this comes back
   * that UI is gone.
   */
  aborted?: true;
}

export interface DiscoveryMessagesParams {
  limit?: number;
  before?: string;
}

export interface DiscoveryMessagesResponse {
  success: boolean;
  messages: DiscoverySessionMessage[];
  hasMore?: boolean;
  message?: string;
}

export interface DiscoveryContextResponse {
  success: boolean;
  context?: DiscoveryContextState;
  message?: string;
}

export interface DiscoveryInsightsParams {
  limit?: number;
  before?: string;
  cursor?: string;
}

export interface DiscoveryInsightsResponse {
  success: boolean;
  insights: DiscoverySessionInsight[];
  hasMore?: boolean;
  message?: string;
}

export interface DiscoverySessionsResponse {
  success: boolean;
  sessions: DiscoverySession[];
  message?: string;
}

/** Idea created from / linked to a discovery artifact in a session (getSessionIdeas). */
export interface SessionDiscoveryIdea {
  _id: string;
  title?: string;
  /** Matches {@link Artifact.alias} for linking cards to ideas */
  artifactId?: string;
  /** Alternate field name from API */
  artifactsId?: string;
}

export interface SessionIdeasResponse {
  success: boolean;
  ideas: SessionDiscoveryIdea[];
  message?: string;
}

export interface DiscoveryPromptsResponse {
  success: boolean;
  prompts: unknown[];
  message?: string;
}


export interface OnePager {
  _id: string;
  sessionId: string;
  messageId: string;
  artifactId?: string;
  queryText: string;
  title: string;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOnePagerVariables {
  sessionId: string;
  messageId: string;
  /** Optional — links this one-pager to an artifact when created from the insights panel */
  artifactId?: string;
  /** The preceding user query text */
  queryText: string;
  /** ML-generated title to persist */
  title?: string;
  /** ML-generated description to persist */
  description?: string;
}

// ----- Error handling -----

const HTTP_ACCEPTED = 202;
const HTTP_NOT_FOUND = 404;

const handleDiscoveryApiError = <T>(error: unknown, fallback: T): T => {
  const err = error as { response?: { status?: number; data?: { message?: string } }; request?: unknown; message?: string };
  if (err.response) {
    const { status, data } = err.response;
    console.error('[Discovery API] HTTP Error:', { status, data });
    return { ...fallback, message: data?.message || `Server error: ${status}` } as T;
  }
  if (err.request) {
    console.error('[Discovery API] Network Error:', err.message);
    return { ...fallback, message: 'Network error. Please check your connection.' } as T;
  }
  console.error('[Discovery API] Error:', err.message);
  return { ...fallback, message: err.message || 'An unexpected error occurred' } as T;
};

// ----- API functions -----

export const sendDiscoveryMessage = async (
  sessionId: string,
  message: string,
  auth0: { getAccessTokenSilently: () => Promise<string> },
  uiFilters?: SessionFilter[],
  options?: { hidden?: boolean; clientRequestId?: string }
): Promise<SendDiscoveryMessageResponse> => {
  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    const body: SendDiscoveryMessageRequest = { message, sessionId };
    if (uiFilters != null) {
      body.uiFilters = uiFilters;
    }
    if (options?.hidden != null) {
      body.hidden = options.hidden;
    }
    if (options?.clientRequestId) {
      body.clientRequestId = options.clientRequestId;
    }
    const response = await axios.post(
      `${DISCOVERY_BASE}/chat`,
      body,
      config
    );
    if (response.status === HTTP_ACCEPTED) {
      return { success: false, sessionId, status: 'pending' };
    }
    return { success: true, ...response.data };
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      sessionId: '',
      message: 'Failed to send message',
    });
  }
};

// ----- Idempotent send with recovery -----

/**
 * How long to keep asking the backend for a dropped request's result. Matches
 * the server's own discovery chat ceiling (DISCOVERY_CHAT_REQUEST_TIMEOUT_MS,
 * 10 min) — polling past that can only ever see a terminal state.
 *
 * Only appropriate where nothing blocks on the answer. A caller that awaits
 * the result inline must pass a shorter `recoveryBudgetMs`, or it will sit
 * there for the full ten minutes.
 */
const CHAT_REQUEST_RECOVERY_BUDGET_MS = 10 * 60 * 1000;
const CHAT_REQUEST_POLL_INTERVAL_MS = 3000;
const CHAT_REQUEST_POLL_MAX_INTERVAL_MS = 15000;

/**
 * A chat-request row is created before the agent is invoked, so a 404 means
 * the POST never reached the server rather than "not ready yet". Tolerate a
 * few in case the read races the insert, then stop.
 */
const MAX_CONSECUTIVE_NOT_FOUND_POLLS = 5;

export type DiscoveryChatRequestStatus =
  | { status: 'pending' }
  | { status: 'completed'; response: Record<string, unknown> }
  | { status: 'failed'; error?: string }
  | { status: 'not_found' };

export const getDiscoveryChatRequest = async (
  clientRequestId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> },
  signal?: AbortSignal
): Promise<DiscoveryChatRequestStatus> => {
  try {
    const response = await axios.get(
      `${DISCOVERY_BASE}/chat/requests/${encodeURIComponent(clientRequestId)}`,
      signal ? { signal } : undefined
    );
    return response.data as DiscoveryChatRequestStatus;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === HTTP_NOT_FOUND) return { status: 'not_found' };
    throw error;
  }
};

/** Fresh per send — this is the dedupe key, never derive it from the message. */
export const newClientRequestId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `cr-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Resolves early when aborted, so an unmount does not have to wait out a
 * poll interval before the loop notices it should stop.
 */
const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const settle = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', settle);
      resolve();
    };
    const timer = setTimeout(settle, ms);
    signal?.addEventListener('abort', settle, { once: true });
  });

/** We stopped waiting on the caller's behalf; the send itself carries on. */
const abandonedResult = (sessionId: string): SendDiscoveryMessageResponse => ({
  success: false,
  sessionId,
  status: 'pending',
  aborted: true,
});

/**
 * Only transport-level failures are recoverable: the request may well have
 * landed and the agent may still be running. An HTTP error the server chose
 * to send (400/403/…) is a real answer and must not be retried.
 */
function isRecoverableTransportError(error: unknown): boolean {
  const err = error as { response?: { status?: number }; request?: unknown };
  if (!err.response) return Boolean(err.request);
  const status = err.response.status;
  return status === 502 || status === 503 || status === 504;
}

async function pollChatRequestUntilTerminal(
  clientRequestId: string,
  sessionId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> },
  options?: { recoveryBudgetMs?: number; signal?: AbortSignal }
): Promise<SendDiscoveryMessageResponse> {
  const deadline =
    Date.now() + (options?.recoveryBudgetMs ?? CHAT_REQUEST_RECOVERY_BUDGET_MS);
  let intervalMs = CHAT_REQUEST_POLL_INTERVAL_MS;
  let consecutiveNotFound = 0;

  while (Date.now() < deadline) {
    if (options?.signal?.aborted) {
      return abandonedResult(sessionId);
    }
    await delay(
      Math.min(intervalMs, Math.max(0, deadline - Date.now())),
      options?.signal
    );
    intervalMs = Math.min(intervalMs * 2, CHAT_REQUEST_POLL_MAX_INTERVAL_MS);
    if (options?.signal?.aborted) {
      return abandonedResult(sessionId);
    }

    let state: DiscoveryChatRequestStatus;
    try {
      state = await getDiscoveryChatRequest(clientRequestId, auth0, options?.signal);
    } catch {
      continue;
    }

    if (state.status === 'not_found') {
      consecutiveNotFound += 1;
      if (consecutiveNotFound >= MAX_CONSECUTIVE_NOT_FOUND_POLLS) {
        return { success: false, sessionId, message: 'Failed to send message' };
      }
      continue;
    }
    consecutiveNotFound = 0;

    if (state.status === 'completed') {
      return { success: true, sessionId, ...state.response };
    }
    if (state.status === 'failed') {
      return {
        success: false,
        sessionId,
        message: state.error ?? 'The analysis failed. Please try again.',
      };
    }
  }

  return {
    success: false,
    sessionId,
    status: 'pending',
    message: 'The analysis is still running. It will appear here once it finishes.',
  };
}

/**
 * Send a chat message that survives losing the browser request.
 *
 * The agent can run for minutes; if the connection dies in the meantime the
 * plain send resolves as a failure and the completed answer — artifacts,
 * suggested-idea payload and limit fields included — is stranded server-side,
 * recoverable only by accident through the session's `chatStatus` polling.
 * Carrying a `clientRequestId` lets us ask for that exact result instead.
 */
export const sendDiscoveryMessageWithRecovery = async (
  sessionId: string,
  message: string,
  auth0: { getAccessTokenSilently: () => Promise<string> },
  uiFilters?: SessionFilter[],
  options?: {
    hidden?: boolean;
    /** Cap the recovery poll. Required when a UI awaits this result inline. */
    recoveryBudgetMs?: number;
    signal?: AbortSignal;
  }
): Promise<SendDiscoveryMessageResponse> => {
  const clientRequestId = newClientRequestId();
  const recovery = {
    ...(options?.recoveryBudgetMs != null && {
      recoveryBudgetMs: options.recoveryBudgetMs,
    }),
    ...(options?.signal && { signal: options.signal }),
  };
  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    const body: SendDiscoveryMessageRequest = { message, sessionId, clientRequestId };
    if (uiFilters != null) body.uiFilters = uiFilters;
    if (options?.hidden != null) body.hidden = options.hidden;

    const response = await axios.post(`${DISCOVERY_BASE}/chat`, body, config);
    if (response.status === HTTP_ACCEPTED) {
      return pollChatRequestUntilTerminal(clientRequestId, sessionId, auth0, recovery);
    }
    return { success: true, ...response.data };
  } catch (error) {
    // Aborted while the POST was in flight. The send is not cancelled with it
    // — it may well have landed — and there is no longer anyone to report to,
    // so classify this as abandoned instead of failed.
    if (options?.signal?.aborted) {
      return abandonedResult(sessionId);
    }
    if (isRecoverableTransportError(error)) {
      return pollChatRequestUntilTerminal(clientRequestId, sessionId, auth0, recovery);
    }
    return handleDiscoveryApiError(error, {
      success: false,
      sessionId: '',
      message: 'Failed to send message',
    });
  }
};

export const createDiscoverySession = async (
  auth0: { getAccessTokenSilently: () => Promise<string> },
  uiFilters?: SessionFilter[],
  parentSessionId?: string
): Promise<{ success: boolean; session?: DiscoverySession; sessionId?: string; message?: string }> => {
  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    const body: Record<string, unknown> = {};
    if (uiFilters != null) body.uiFilters = uiFilters;
    if (parentSessionId) body.parentSessionId = parentSessionId;
    const response = await axios.post(`${DISCOVERY_BASE}/sessions`, body, config);
    return response.data;
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      message: 'Failed to create session',
    });
  }
};

export const getDiscoverySessions = async (
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<DiscoverySessionsResponse> => {
  try {
    const response = await axios.get(`${DISCOVERY_BASE}/sessions`);
    return response.data;
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      sessions: [],
      message: 'Failed to get sessions',
    });
  }
};

export const getDiscoverySession = async (
  sessionId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<DiscoverySession | null> => {
  try {
    const response = await axios.get(
      `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}`
    );
    return response.data as DiscoverySession;
  } catch {
    return null;
  }
};

export const updateDiscoverySessionFilters = async (
  sessionId: string,
  sessionFilters: SessionFilter[],
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<void> => {
  const config = { headers: { 'Content-Type': 'application/json' } };
  await axios.patch(
    `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/filters`,
    { sessionFilters },
    config
  );
};

export const shareDiscoverySession = async (
  sessionId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<{ success: boolean }> => {
  const response = await axios.patch(
    `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/share`,
    {}
  );
  return response.data;
};

/** Map GET /messages payload so `feedback` matches {@link DiscoverySessionMessage} after refresh. */
function normalizeDiscoveryMessageFeedback(
  raw: DiscoverySessionMessage['feedback'] | Record<string, unknown> | undefined
): DiscoverySessionMessage['feedback'] | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const rating = o.rating;
  if (rating !== 'helpful' && rating !== 'not_helpful') return undefined;
  const updatedAt =
    (typeof o.updatedAt === 'string' && o.updatedAt) ||
    (typeof o.updated_at === 'string' && o.updated_at) ||
    new Date().toISOString();
  return { rating, updatedAt };
}

function normalizeDiscoverySessionMessage(msg: DiscoverySessionMessage): DiscoverySessionMessage {
  const feedback = normalizeDiscoveryMessageFeedback(msg.feedback as Record<string, unknown> | undefined);
  const { feedback: _drop, ...rest } = msg;
  const base = feedback ? { ...rest, feedback } : (rest as DiscoverySessionMessage);
  if (base.content?.trimStart().startsWith('IDEA_CREATED')) {
    return { ...base, messageType: 'idea_created' as const };
  }
  return base;
}

export const getDiscoveryMessages = async (
  sessionId: string,
  params: DiscoveryMessagesParams = {},
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<DiscoveryMessagesResponse> => {
  try {
    const query = new URLSearchParams();
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.before) query.set('before', params.before);
    const url = `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/messages?${query.toString()}`;
    const response = await axios.get(url);
    const data = response.data as DiscoveryMessagesResponse;
    return {
      ...data,
      messages: Array.isArray(data.messages)
        ? data.messages
          .map((m) => normalizeDiscoverySessionMessage(m as DiscoverySessionMessage))
          // Never surface suggested_idea responses in the thread — the modal handles them
          .filter((m) => m.metadata?.artifacts?.[0]?.type !== 'suggested_idea')
        : [],
    };
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      messages: [],
      message: 'Failed to get messages',
    });
  }
};

export const getDiscoveryContext = async (
  sessionId: string,
  at: string | undefined,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<DiscoveryContextResponse> => {
  try {
    const query = at ? `?at=${encodeURIComponent(at)}` : '';
    const url = `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/context${query}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      message: 'Failed to get context',
    });
  }
};

export const getDiscoveryInsights = async (
  sessionId: string,
  params: DiscoveryInsightsParams = {},
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<DiscoveryInsightsResponse> => {
  try {
    const query = new URLSearchParams();
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.before) query.set('before', params.before);
    if (params.cursor) query.set('cursor', params.cursor);
    const url = `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/insights?${query.toString()}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      insights: [],
      message: 'Failed to get insights',
    });
  }
};

export const getSessionIdeas = async (
  sessionId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<SessionIdeasResponse> => {
  try {
    const url = `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/sessionIdeas`;
    const response = await axios.get(url);
    const data = response.data as SessionIdeasResponse;
    return {
      success: data.success !== false,
      ideas: Array.isArray(data.ideas) ? data.ideas : [],
      message: data.message,
    };
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      ideas: [],
      message: 'Failed to load session ideas',
    });
  }
};

/**
 * Resolve a single artifact by its `_id`. Returns `null` on 404/403 so the
 * deep-link loader can silently fall back (and strip the query param) when
 * the artifact isn't accessible from the current account.
 */
export const fetchArtifactById = async (
  artifactId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<DiscoveryArtifactDeepLinkResponse | null> => {
  try {
    const response = await axios.get(
      `${DISCOVERY_BASE}/artifacts/${encodeURIComponent(artifactId)}`
    );
    return response.data as DiscoveryArtifactDeepLinkResponse;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404 || status === 403) return null;
    console.error('[Discovery API] fetchArtifactById failed:', error);
    return null;
  }
};

export const updateMessageFeedback = async (
  sessionId: string,
  messageId: string,
  rating: 'helpful' | 'not_helpful' | null,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<void> => {
  const config = { headers: { 'Content-Type': 'application/json' } };
  await axios.patch(
    `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
    { rating },
    config
  );
};

export const updateMessageHidden = async (
  sessionId: string,
  messageId: string,
  hidden: boolean,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<void> => {
  const config = { headers: { 'Content-Type': 'application/json' } };
  await axios.patch(
    `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}`,
    { hidden },
    config
  );
};

export const getDiscoveryPrompts = async (
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<DiscoveryPromptsResponse> => {
  try {
    const response = await axios.get(`${DISCOVERY_BASE}/prompts`);
    return response.data;
  } catch (error) {
    return handleDiscoveryApiError(error, {
      success: false,
      prompts: [],
      message: 'Failed to get prompts',
    });
  }
};

/** Idea row returned by related-ideas (evidence cluster → ideas). */
export interface RelatedIdeaByEvidence {
  _id: string;
  title: string;
  status: string;
  matchCount: number;
  /** assigned + strong-suggested evidence count, same denominator as the idea page total */
  ideaEvidenceTotal: number;
  ownerName: string;
  aggregations: {
    accountsCount?: { total?: number; assigned?: number; suggested?: number };
  };
}

export const fetchRelatedIdeasByEvidenceIds = async (
  evidenceIds: string[],
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<{ ideas: RelatedIdeaByEvidence[] }> => {
  try {
    const config = { headers: { 'Content-Type': 'application/json' } };
    const response = await axios.post(
      `${AI_AGENT_EVIDENCES_BASE}/related-ideas`,
      { evidence_ids: evidenceIds },
      config
    );
    return response.data;
  } catch (error) {
    return handleDiscoveryApiError(error, {
      ideas: [],
    });
  }
};


export const createOnePager = async (
  vars: CreateOnePagerVariables,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<OnePager> => {
  const { sessionId, messageId, ...body } = vars;
  const config = { headers: { 'Content-Type': 'application/json' } };
  const { data } = await axios.post<OnePager>(
    `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/one-pager`,
    body,
    config
  );
  return data;
};

export const fetchOnePager = async (
  sessionId: string,
  messageId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<OnePager | null> => {
  try {
    const { data } = await axios.get<OnePager>(
      `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/one-pager`
    );
    return data;
  } catch (e: any) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
};


export const fetchSessionOnePagers = async (
  sessionId: string,
  auth0: { getAccessTokenSilently: () => Promise<string> }
): Promise<OnePager[]> => {
  const { data } = await axios.get(
    `${DISCOVERY_BASE}/sessions/${encodeURIComponent(sessionId)}/one-pager`
  );
  // Normalise: handle direct array OR wrapped response ({ onePagers: [...] } / { data: [...] })
  if (Array.isArray(data)) return data as OnePager[];
  if (Array.isArray(data?.onePagers)) return data.onePagers as OnePager[];
  if (Array.isArray(data?.data)) return data.data as OnePager[];
  return [];
};

// ----- Query key factory -----

export const discoveryKeys = {
  all: ['discovery'] as const,
  sessions: () => [...discoveryKeys.all, 'sessions'] as const,
  messages: (sessionId: string) => [...discoveryKeys.all, 'messages', sessionId] as const,
  context: (sessionId: string, at?: string) =>
    [...discoveryKeys.all, 'context', sessionId, at ?? 'latest'] as const,
  insights: (sessionId: string, before?: string) =>
    [...discoveryKeys.all, 'insights', sessionId, before ?? 'all'] as const,
  prompts: () => [...discoveryKeys.all, 'prompts'] as const,
  relatedIdeas: (evidenceIdsKey: string) =>
    [...discoveryKeys.all, 'relatedIdeas', evidenceIdsKey] as const,
  sessionIdeas: (sessionId: string) =>
    [...discoveryKeys.all, 'sessionIdeas', sessionId] as const,
  artifact: (artifactId: string) => [...discoveryKeys.all, 'artifact', artifactId] as const,
  onePager: (sessionId: string, messageId: string) =>
    [...discoveryKeys.all, 'one-pager', sessionId, messageId] as const,
  onePagerGenerate: (sessionId: string, messageId: string) =>
    [...discoveryKeys.all, 'one-pager-generate', sessionId, messageId] as const,
  sessionOnePagers: (sessionId: string) =>
    [...discoveryKeys.all, 'session-one-pagers', sessionId] as const,
};